import { Button, FormControl, GridProps, SimpleGrid, Text, TextProps, useDisclosure } from "@chakra-ui/react";
import { useCaravanDataContext } from "../CaravanDataContext";
import { ContactFormModal } from "../components/ContactFormModal";
import { BEDROOMS, MAX_CONTENT_WIDTH, MAX_PRICES, MIN_PRICES } from "../const";
import { DEFAULT_FIRST_FORM_VALUE, useFilterStore } from "../stores/FilterStore";
import { capitalize, formatNumberWithPounds } from "../utils";
import { SelectInput } from "./FormComponents";

// This is form in the header of the homepage
export const SearchFilterForm: React.FC<GridProps> = (props) => {
  const { county, setCounty, bedrooms, setBedrooms, minPrice, setMinPrice, maxPrice, setMaxPrice } = useFilterStore([
    "county",
    "setCounty",
    "bedrooms",
    "setBedrooms",
    "minPrice",
    "setMinPrice",
    "maxPrice",
    "setMaxPrice",
  ]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { countyNames } = useCaravanDataContext();

  const formDisabled = county === DEFAULT_FIRST_FORM_VALUE;

  const minPricesToDisplay = isNaN(parseFloat(maxPrice))
    ? MIN_PRICES
    : MIN_PRICES.filter((price) => Number(price) < Number(maxPrice));

  const maxPricesToDisplay = isNaN(parseFloat(minPrice))
    ? MAX_PRICES
    : MAX_PRICES.filter((price) => Number(price) > Number(minPrice));

  function updateBlankState<T>(currentValue: T, setter: (value: T | "any") => void) {
    if (currentValue === DEFAULT_FIRST_FORM_VALUE) {
      setter("any");
    }
  }

  const submitForm = () => {
    if (formDisabled) return;

    updateBlankState(bedrooms, setBedrooms);
    updateBlankState(minPrice, setMinPrice);
    updateBlankState(maxPrice, setMaxPrice);

    onOpen();
  };

  return (
    <>
      <ContactFormModal isOpen={isOpen} onClose={onClose} />
      <form>
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 5 }}
          spacingX={{ base: 2, md: 4, xl: 6 }}
          spacingY={{ base: 2, md: 2, xl: 6 }}
          mb={8}
          px={4}
          borderRadius="10px"
          maxW={MAX_CONTENT_WIDTH}
          mx="auto"
          {...props}
        >
          <FormControl>
            <FormLabel>Region</FormLabel>
            <SelectInput placeholder="Search by Region" value={county} onChange={(e) => setCounty(e.target.value)}>
              {countyNames.map((county) => (
                <option key={county} value={county}>
                  {capitalize(county)}
                </option>
              ))}
            </SelectInput>
          </FormControl>

          <FormControl>
            <FormLabel>Bedrooms</FormLabel>
            <SelectInput placeholder="No of Bedrooms" value={bedrooms} onChange={(e) => setBedrooms(e.target.value)}>
              <option value="any">Any</option>
              {BEDROOMS.map((bedroom) => (
                <option key={bedroom} value={bedroom}>
                  {bedroom} +
                </option>
              ))}
            </SelectInput>
          </FormControl>

          <FormControl>
            <FormLabel>Min Price</FormLabel>
            <SelectInput placeholder="Minimum Price" value={minPrice} onChange={(e) => setMinPrice(e.target.value)}>
              <option value="any">Any</option>
              {minPricesToDisplay.map((price) => (
                <option key={`min${price}`} value={price}>
                  {formatNumberWithPounds(price)}
                </option>
              ))}
            </SelectInput>
          </FormControl>

          <FormControl>
            <FormLabel>Max Price</FormLabel>
            <SelectInput placeholder="Maximum Price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)}>
              <option value="any">Any</option>
              {maxPricesToDisplay.map((price) => (
                <option key={`max${price}`} value={price}>
                  {formatNumberWithPounds(price)}
                </option>
              ))}
            </SelectInput>
          </FormControl>

          <Button
            variant="yellow"
            gridColumn={{ base: "1 / -1", md: "1 / -1", lg: "auto" }}
            fontWeight="bold"
            borderRadius="20px"
            borderColor="gray.200"
            borderWidth="1px"
            mt={6}
            isDisabled={formDisabled}
            onClick={submitForm}
          >
            Search Now
          </Button>
        </SimpleGrid>
      </form>
    </>
  );
};

const FormLabel: React.FC<TextProps> = (props) => (
  <Text
    textAlign="left"
    color="white"
    fontWeight={{ base: "400", md: "500" }}
    fontSize={{ base: "md", md: "lg" }}
    {...props}
  />
);
